import { FC, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import CssBaseline from '@mui/material/CssBaseline';
import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import 'src/global.css';
import 'src/locales/i18n';
import { RTL } from 'src/components/rtl';
import { SettingsDrawer } from 'src/components/settings/settings-drawer';
import { Toaster } from 'src/components/toaster';
import { SettingsConsumer, SettingsProvider } from 'src/contexts/settings';
import { useNprogress } from 'src/hooks/use-nprogress';
import { createTheme } from 'src/theme';
import Router from './routes/routes';
import { PublicInvoiceDownload } from '@restore/rebill';
import { StopwatchProvider } from './layouts/check-in-checkout/StopwatchContext';
import { paths } from './paths';
import DataDogRum from './components/dataDogRum/DataDogRum';
import AuthContext, { getRealmName } from './routes/AuthContext';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { resetTherapistProfile } from './redux/actions/facilityActions';
import { localhostSplits } from './localhostSplits';
import { baseApiUrl, splitIoAuthorizationKey } from './config';
import { TherapyPlanningProvider } from './components/therapyPlanning_2.0/therapyPlanningProvider';
import InactivityLogout from './components/InactivityLogout';
import { Layout as AuthLayout } from './layouts/auth/classic-layout';
import ForgotPassword from './pages/auth/forgot-password';
import { getFacilityId } from './hooks/use-facility-id';

//  MUST stay a stable module-level reference. `splitio-react` caches factories in a Map keyed on
//  this object's *identity* and SplitFactoryProvider's effect deps are `[config]`, so a config
//  literal built inside <App> destroys and re-bootstraps the entire SDK on every render — twice per
//  navigation, because useNprogress subscribes <App> to useLocation and then sets state. Each
//  bootstrap is a full /splitChanges?since=-1 + /mySegments + /v2/auth round trip, resets the
//  impression dedup cache, and blanks every treatment until SDK_READY fires again (visible as
//  feature-gated UI flickering off after each navigation).
const sdkConfig: SplitIO.IBrowserSettings = {
  core: {
    // Setting authorizationKey to 'localhost' for localhost development
    // You can learn more here: https://help.split.io/hc/en-us/articles/360038825091-React-SDK#localhost-mode
    // authorizationKey: hostName === 'localhost' ? 'localhost' : import.meta.env.VITE_SPLIT_IO_KEY,
    authorizationKey: splitIoAuthorizationKey,
    //  Key the Split factory on the TENANT (realm), not the facility. Physicians have no
    //  facility, so a facilityId key is empty for them and the factory never becomes ready —
    //  breaking every treatment (incl. the tenant-scoped signature-validation flag) for the
    //  whole session. Facility/enterprise treatments still re-key per useSplitClient.
    //
    //  Safe to resolve once at module load: the realm comes from the hostname (or localStorage on
    //  localhost) and cannot change without a full page load.
    key: getRealmName()?.toLowerCase() || getFacilityId() || 'anonymous',
  },
  storage: {
    //  Persist the rollout plan so a reload resumes from a `since` cursor instead of refetching
    //  every flag definition, and so impression dedup survives page loads.
    type: 'LOCALSTORAGE',
    prefix: 'restore-provider-portal',
  },
  sync: {
    //  These are release toggles, not experiments — we don't read per-impression data or run
    //  metrics off them in the Split UI. NONE sends deduplicated evaluation counts instead of one
    //  impression per evaluation, which is the bulk of this app's Split ingest.
    impressionsMode: 'NONE',
  },
  // Mocking features/splits if running in localhost
  //  features: hostName === 'localhost' ? localhostSplits : undefined,
};

export const App: FC = () => {
  useNprogress();
  const { landingPage } = paths;
  const hostName = window.location.hostname;
  const pathName = window.location.pathname;
  const auth = useContext(AuthContext);
  const isAuthenticated = auth.authenticated;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isAuthenticated) return;
    if (hostName === 'localhost' && auth.realm === pathName.split('/')[1]) {
      navigate(landingPage);
      return;
    }

    if (hostName !== 'localhost' && pathName === '/') {
      navigate(landingPage);
      return;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) return;
    dispatch(resetTherapistProfile());
  }, [isAuthenticated]);

  // BOPS-184/185: public secure invoice-download page — external billing recipient, no app login.
  // Render BEFORE the auth gate so Keycloak check-sso never escalates to a login redirect. The UI is
  // the rebill PublicInvoiceDownload component (@restore/rebill); provider-portal supplies host
  // context only. The component appends /api/billing/..., so strip the trailing /api from
  // VITE_API_BASE_URL (avoids the /api/api double-404).
  if (pathName.endsWith(paths.invoiceDownload)) {
    const token = new URLSearchParams(window.location.search).get('t')?.trim() ?? '';
    const tenantId =
      hostName === 'localhost' || hostName === '127.0.0.1'
        ? window.location.pathname.split('/')[1] ?? ''
        : hostName.split('-')[0];
    const apiBaseUrl = (baseApiUrl ?? '').replace(/\/api\/?$/, '');
    return (
      <>
        <Helmet>
          <title>Secure invoice download</title>
        </Helmet>
        <CssBaseline />
        <PublicInvoiceDownload
          apiBaseUrl={apiBaseUrl}
          tenantId={tenantId}
          token={token}
        />
      </>
    );
  }

  // APIT-696 — the self-service "forgot password" email-entry page must render for a signed-out
  // visitor; that is the entire point of the feature. Exempt it from the auth gate below the same
  // way BOPS-182/184 exempt /invoice-download above: render it BEFORE the isAuthenticated ternary,
  // so `auth.login()` (which bounces the browser to the Keycloak login page) never runs for this
  // path. `endsWith` also covers the localhost `/{realm}/…` form. Deliberately NOT routed through
  // <Router/> (routes.tsx) — that keeps this exemption to exactly this one path, with none of the
  // authenticated app's providers (Split, Datadog RUM, the stopwatch/therapy-planning contexts,
  // inactivity logout) mounted while the visitor has no session.
  if (pathName.endsWith(paths.auth.forgotPassword)) {
    const theme = createTheme({
      colorPreset: 'indigo',
      contrast: 'normal',
      direction: 'ltr',
      paletteMode: 'light',
      responsiveFontSizes: true,
    });
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthLayout>
          <ForgotPassword />
        </AuthLayout>
      </ThemeProvider>
    );
  }

  return isAuthenticated ? (
    <SplitFactoryProvider config={sdkConfig}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SettingsProvider>
          <SettingsConsumer>
            {(settings) => {
              if (!settings.isInitialized) {
                // return null;
              }
              const theme = createTheme({
                colorPreset: 'indigo',
                contrast: 'normal',
                direction: 'ltr',
                paletteMode: 'light',
                responsiveFontSizes: settings.responsiveFontSizes,
              });

              return (
                <ThemeProvider theme={theme}>
                  <Helmet>
                    <meta
                      name="color-scheme"
                      content={'light'}
                    />
                    <meta
                      name="theme-color"
                      content={theme.palette.neutral[900]}
                    />
                  </Helmet>
                  <RTL direction={'ltr'}>
                    <CssBaseline />
                    <InactivityLogout />
                    <StopwatchProvider>
                      <TherapyPlanningProvider>
                        <Router />
                      </TherapyPlanningProvider>
                    </StopwatchProvider>
                    <SettingsDrawer
                      canReset={settings.isCustom}
                      onClose={settings.handleDrawerClose}
                      onReset={settings.handleReset}
                      onUpdate={settings.handleUpdate}
                      open={settings.openDrawer}
                      values={{
                        colorPreset: 'indigo',
                        contrast: 'normal',
                        direction: 'ltr',
                        paletteMode: 'light',
                        responsiveFontSizes: settings.responsiveFontSizes,
                        stretch: settings.stretch,
                        layout: 'vertical',
                        navColor: 'discrete',
                      }}
                    />
                    <Toaster />
                    <DataDogRum />
                  </RTL>
                </ThemeProvider>
              );
            }}
          </SettingsConsumer>
        </SettingsProvider>
      </LocalizationProvider>
    </SplitFactoryProvider>
  ) : (
    <>{auth.login()}</>
  );
};
